import * as React from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CodeIcon from "@mui/icons-material/Code";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import JsonDataModal from "../components/admin/JsonDataModal";
import SquarespacePageHeader from "../components/SquarespacePageHeader";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

export default function SquarespacePriceDetail() {
  const { id } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const [pricingItem, setPricingItem] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [jsonModalOpen, setJsonModalOpen] = React.useState(false);

  React.useEffect(() => {
    const fetchPricingItem = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently().catch(() => null);
        const response = await fetch(
          `${API_BASE_URL}/admin/squarespace/prices/${id}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(
            errData.message || `Failed to fetch pricing: ${response.status} ${response.statusText}`
          );
        }

        const responseData = await response.json();
        setPricingItem(responseData.data || null);
      } catch (err) {
        console.error("Error fetching pricing item:", err);
        setError(err.message || "Failed to fetch pricing item");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPricingItem();
    }
  }, [id, getAccessTokenSilently]);

  // Helper to format currency
  const formatCurrency = (amount, currency = "USD") => {
    if (amount === undefined || amount === null) return "N/A";
    const value = amount > 10000 ? amount / 100 : amount;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(value);
  };

  const productName = pricingItem?.productName || pricingItem?.name || pricingItem?.planName || pricingItem?.tld || pricingItem?.extension || id;
  const type = pricingItem?.type || pricingItem?.category || (pricingItem?.tld ? 'domain' : 'website');

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/squarespace"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Squarespace
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/squarespace/prices"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Prices
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  {productName}
                </Typography>
              </Breadcrumbs>
            </Box>

            <SquarespacePageHeader
              title="Pricing Details"
              description="View detailed pricing information"
              icon={<AttachMoneyIcon sx={{ color: "primary.main" }} />}
            >
              {pricingItem && (
                <Button
                  variant="outlined"
                  startIcon={<CodeIcon />}
                  onClick={() => setJsonModalOpen(true)}
                >
                  View JSON
                </Button>
              )}
            </SquarespacePageHeader>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : pricingItem ? (
              <>
              <Card>
                <CardContent sx={{ pt: 2, pb: 2 }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 1.5 }}>
                    Pricing Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Product Name
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {productName}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Type
                      </Typography>
                      <Box sx={{ mt: 0.25 }}>
                        <Chip
                          label={type}
                          size="small"
                          color={type === 'domain' ? 'primary' : 'secondary'}
                        />
                      </Box>
                    </Grid>
                    {pricingItem.id && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          ID
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }} color="text.secondary" fontFamily="monospace">
                          {pricingItem.id}
                        </Typography>
                      </Grid>
                    )}
                    {(pricingItem.price || pricingItem.monthlyPrice || pricingItem.annualPrice) && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Price
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {formatCurrency(pricingItem.price || pricingItem.monthlyPrice || pricingItem.annualPrice)}
                          {pricingItem.monthlyPrice && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                              /month
                            </Typography>
                          )}
                          {pricingItem.annualPrice && !pricingItem.monthlyPrice && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                              /year
                            </Typography>
                          )}
                        </Typography>
                      </Grid>
                    )}
                    {pricingItem.registrationPrice && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Registration Price
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {formatCurrency(pricingItem.registrationPrice)}
                        </Typography>
                      </Grid>
                    )}
                    {pricingItem.renewalPrice && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Renewal Price
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {formatCurrency(pricingItem.renewalPrice)}
                        </Typography>
                      </Grid>
                    )}
                    {pricingItem.transferPrice && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Transfer Price
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {formatCurrency(pricingItem.transferPrice)}
                        </Typography>
                      </Grid>
                    )}
                    {pricingItem.tld && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          TLD
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {pricingItem.tld}
                        </Typography>
                      </Grid>
                    )}
                    {pricingItem.extension && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Extension
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {pricingItem.extension}
                        </Typography>
                      </Grid>
                    )}
                    {pricingItem.planName && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Plan Name
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {pricingItem.planName}
                        </Typography>
                      </Grid>
                    )}
                    {Object.entries(pricingItem).map(([key, value]) => {
                      // Skip already displayed fields
                      const displayedFields = ['id', 'name', 'planName', 'tld', 'extension', 'type', 'category', 'price', 'monthlyPrice', 'annualPrice', 'registrationPrice', 'renewalPrice', 'transferPrice'];
                      if (displayedFields.includes(key)) return null;
                      if (value === null || value === undefined) return null;
                      
                      return (
                        <Grid key={key} size={{ xs: 12, sm: 6, md: 4 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.25 }}>
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </Typography>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>

              <Box sx={{ mt: 2, p: 2, borderRadius: 1, backgroundColor: "action.hover" }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Link pricing to Subscription Plan in BryteBridge
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  To link this Squarespace pricing to a Subscription Plan, add <code style={{ fontFamily: "monospace" }}>squarespace_pricing_id</code> to the plan&apos;s meta_data:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: (theme) => theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
                    fontSize: "0.875rem",
                    fontFamily: "monospace",
                    overflow: "auto",
                  }}
                >
                  {JSON.stringify(
                    { squarespace_pricing_id: pricingItem?.id || id || "—" },
                    null,
                    2
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Go to Subscription Plan detail → Edit meta_data → add <code style={{ fontFamily: "monospace" }}>squarespace_pricing_id</code> with the value above.
                </Typography>
              </Box>
              </>
            ) : (
              <Alert severity="warning">
                Pricing item not found
              </Alert>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>
      <JsonDataModal
        open={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        data={pricingItem}
        title={`JSON Data - ${productName}`}
      />
    </>
  );
}
